import { useEffect, useState } from 'react';
import api from '../api/axios';
import './MyAssignedLeads.css';

function MyAssignedLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [editStatus, setEditStatus] = useState('new');
  const [editNotes, setEditNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpLeadId, setFollowUpLeadId] = useState(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [filterFollowUp, setFilterFollowUp] = useState(false);

  useEffect(() => {
    fetchMyLeads();
  }, []);

  const fetchMyLeads = async () => {
    setLoading(true);
    try {
      const response = await api.get('/lead-assignments/my-leads');
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (lead) => {
    setEditingLeadId(lead._id);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
  };

  const handleSaveEdit = async (leadId) => {
    try {
      await api.put(`/lead-assignments/${leadId}`, {
        status: editStatus,
        notes: editNotes
      });
      setEditingLeadId(null);
      fetchMyLeads();
    } catch (error) {
      alert('Error updating lead');
    }
  };

  const handleCancel = () => {
    setEditingLeadId(null);
    setEditStatus('');
    setEditNotes('');
  };

  const handleFollowUpClick = (lead) => {
    setFollowUpLeadId(lead._id);
    setFollowUpDate(lead.nextFollowUp ? lead.nextFollowUp.split('T')[0] : '');
    setFollowUpNotes(lead.followUpNotes || '');
    setShowFollowUpModal(true);
  };

  const handleSaveFollowUp = async () => {
    if (!followUpDate) {
      alert('Please select a follow-up date');
      return;
    }

    try {
      await api.put(`/lead-assignments/${followUpLeadId}/follow-up`, {
        nextFollowUp: followUpDate,
        followUpNotes: followUpNotes
      });
      setShowFollowUpModal(false);
      setFollowUpLeadId(null);
      setFollowUpDate('');
      setFollowUpNotes('');
      fetchMyLeads();
    } catch (error) {
      alert('Error setting follow-up');
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      new: '#007bff',
      contacted: '#17a2b8',
      interested: '#28a745',
      negotiating: '#ffc107',
      won: '#20c997',
      lost: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const getStatusIcon = (status) => {
    const icons = {
      new: '🆕',
      contacted: '📞',
      interested: '👍',
      negotiating: '🤝',
      won: '✅',
      lost: '❌'
    };
    return icons[status] || '•';
  };

  const filteredLeads = (() => {
    let result = leads;
    
    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(lead => 
        lead.leadName?.toLowerCase().includes(searchLower) ||
        lead.contactNumber?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.company?.toLowerCase().includes(searchLower) ||
        lead.source?.toLowerCase().includes(searchLower) ||
        lead.product?.toLowerCase().includes(searchLower) ||
        lead.city?.toLowerCase().includes(searchLower) ||
        lead.state?.toLowerCase().includes(searchLower) ||
        lead.designation?.toLowerCase().includes(searchLower) ||
        lead.industry?.toLowerCase().includes(searchLower) ||
        lead.description?.toLowerCase().includes(searchLower) ||
        lead.notes?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by upcoming follow-ups
    if (filterFollowUp) {
      result = result.filter(lead => lead.nextFollowUp && lead.nextFollowUp.trim() !== '');
    }
    
    // Filter by status
    if (filterStatus) {
      result = result.filter(lead => lead.status === filterStatus);
    }
    
    return result;
  })();

  // Count upcoming follow-ups
  const upcomingFollowUpCount = leads.filter(lead => lead.nextFollowUp && lead.nextFollowUp.trim() !== '').length;

  if (loading) {
    return <div className="loading">Loading your leads...</div>;
  }

  return (
    <div className="my-assigned-leads">
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ marginBottom: '15px' }}>📋 My Assigned Leads ({filteredLeads.length} of {leads.length})</h2>
        
        {/* Search Box */}
        <div style={{ marginBottom: '15px' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="🔍 Search leads by name, company, contact, email, product..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '14px',
                background: '#f8fafc',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.background = '#fff';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '4px',
                  borderRadius: '4px'
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Filter by Status */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setFilterStatus('');
              setFilterFollowUp(false);
              setSearchText('');
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              background: filterStatus === '' && !filterFollowUp && !searchText ? '#667eea' : '#e9ecef',
              color: filterStatus === '' && !filterFollowUp ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            All ({leads.length})
          </button>
          {['new', 'contacted', 'interested', 'negotiating', 'won', 'lost'].map(status => {
            const count = leads.filter(l => l.status === status).length;
            return (
              <button
                key={status}
                onClick={() => {
                  setFilterStatus(status);
                  setFilterFollowUp(false);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: filterStatus === status && !filterFollowUp ? getStatusBadgeColor(status) : '#e9ecef',
                  color: filterStatus === status && !filterFollowUp ? 'white' : '#333',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                {getStatusIcon(status)} {status} ({count})
              </button>
            );
          })}
          
          {/* Upcoming Follow-ups Filter Button */}
          <button
            onClick={() => {
              setFilterFollowUp(!filterFollowUp);
              setFilterStatus('');
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              background: filterFollowUp ? '#ff6b6b' : '#e9ecef',
              color: filterFollowUp ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            📞 Upcoming Follow-ups ({upcomingFollowUpCount})
          </button>
        </div>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#999', fontSize: '16px' }}>
            {leads.length === 0 
              ? 'No leads assigned yet' 
              : searchText 
                ? `No leads found matching "${searchText}"` 
                : filterStatus 
                  ? `No leads with status "${filterStatus}"` 
                  : filterFollowUp 
                    ? 'No upcoming follow-ups' 
                    : 'No leads found'
            }
          </p>
          {(searchText || filterStatus || filterFollowUp) && (
            <button
              onClick={() => {
                setSearchText('');
                setFilterStatus('');
                setFilterFollowUp(false);
              }}
              style={{
                marginTop: '15px',
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                background: '#667eea',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {filteredLeads.map((lead) => (
            <div key={lead._id} className="lead-card card">
              {editingLeadId === lead._id ? (
                // Edit Mode
                <div>
                  <div style={{ marginBottom: '15px' }}>
                    <h4>{lead.leadName}</h4>
                    <p style={{ fontSize: '13px', color: '#666', margin: '5px 0' }}>
                      📱 {lead.contactNumber} {lead.email && `| 📧 ${lead.email}`}
                    </p>
                    {lead.company && (
                      <p style={{ fontSize: '13px', color: '#666', margin: '5px 0' }}>
                        🏢 {lead.company}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '5px' }}>Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        <option value="new">🆕 New</option>
                        <option value="contacted">📞 Contacted</option>
                        <option value="interested">👍 Interested</option>
                        <option value="negotiating">🤝 Negotiating</option>
                        <option value="won">✅ Won</option>
                        <option value="lost">❌ Lost</option>
                      </select>
                    </div>
                    {lead.value && (
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '5px' }}>Value</label>
                        <div style={{
                          padding: '8px',
                          background: '#f0f4ff',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#667eea'
                        }}>
                          💰 {lead.value}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '5px' }}>Notes</label>
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px',
                        minHeight: '80px',
                        fontFamily: 'inherit'
                      }}
                      placeholder="Add notes..."
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleCancel}
                      className="btn btn-secondary"
                      style={{ padding: '8px 16px', fontSize: '12px' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSaveEdit(lead._id)}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', fontSize: '12px' }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>{lead.leadName}</h4>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'white',
                        backgroundColor: getStatusBadgeColor(lead.status)
                      }}>
                        {getStatusIcon(lead.status)} {lead.status.toUpperCase()}
                      </span>
                    </div>

                    {/* Lead Details Grid */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                      gap: '16px', 
                      marginTop: '15px',
                      padding: '15px',
                      background: '#f8fafc',
                      borderRadius: '10px'
                    }}>
                      <div>
                        <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Contact Number</p>
                        {lead.contactNumber ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <a 
                              href={`tel:${lead.contactNumber}`} 
                              style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '4px',
                                color: '#667eea',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}
                              title="Click to call"
                            >
                              📞 {lead.contactNumber}
                            </a>
                            <a 
                              href={`https://wa.me/${lead.contactNumber.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                backgroundColor: '#25D366',
                                borderRadius: '50%',
                                textDecoration: 'none',
                                transition: 'transform 0.2s',
                                fontSize: '16px'
                              }}
                              title="WhatsApp"
                              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <svg viewBox="0 0 32 32" width="18" height="18" fill="white">
                                <path d="M16 0C7.164 0 0 7.164 0 16c0 2.777.709 5.39 1.955 7.665L.057 31.293l7.828-2.047A15.932 15.932 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.333c-2.496 0-4.87-.686-6.894-1.986l-.494-.3-5.13 1.342 1.37-4.998-.33-.513A13.288 13.288 0 012.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.335-10.015c-.401-.201-2.379-1.174-2.748-1.308-.369-.134-.638-.201-.906.201-.268.401-1.04 1.308-1.275 1.577-.234.268-.469.302-.87.101-2.412-1.208-3.996-2.155-5.587-4.882-.42-.721.42-.67 1.202-2.231.134-.268.067-.502-.033-.703-.101-.201-.906-2.179-1.241-2.982-.335-.804-.67-.67-.906-.67-.234 0-.502-.033-.77-.033s-.703.1-1.072.502c-.369.401-1.408 1.375-1.408 3.353s1.442 3.888 1.643 4.156c.201.268 2.814 4.295 6.819 6.022 2.513 1.074 3.52 1.174 4.79.988.771-.117 2.379-.972 2.714-1.911.335-.939.335-1.744.234-1.911-.1-.168-.368-.268-.77-.469z"/>
                              </svg>
                            </a>
                          </div>
                        ) : (
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>-</p>
                        )}
                      </div>
                      <div>
                        <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Email</p>
                        <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>📧 {lead.email || '-'}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Company</p>
                        <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>🏢 {lead.company || '-'}</p>
                      </div>
                      {lead.value > 0 && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Lead Value</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '600', color: '#10b981' }}>💰 ₹{lead.value.toLocaleString()}</p>
                        </div>
                      )}
                      {lead.source && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Source</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>📍 {lead.source}</p>
                        </div>
                      )}
                      {lead.product && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Product/Service</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>📦 {lead.product}</p>
                        </div>
                      )}
                      {lead.city && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>City</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>🌆 {lead.city}</p>
                        </div>
                      )}
                      {lead.state && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>State</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>📌 {lead.state}</p>
                        </div>
                      )}
                      {lead.designation && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Designation</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>👤 {lead.designation}</p>
                        </div>
                      )}
                      {lead.industry && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Industry</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>🏭 {lead.industry}</p>
                        </div>
                      )}
                      {lead.leadDate && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Lead Date</p>
                          <p style={{ fontSize: '14px', margin: 0, fontWeight: '500', color: '#334155' }}>📅 {new Date(lead.leadDate).toLocaleDateString()}</p>
                        </div>
                      )}
                    </div>

                    {/* Address if available */}
                    {lead.address && (
                      <div style={{ marginTop: '12px', padding: '10px', background: '#f1f5f9', borderRadius: '6px' }}>
                        <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase' }}>Address</p>
                        <p style={{ fontSize: '13px', margin: 0, color: '#475569' }}>📍 {lead.address}</p>
                      </div>
                    )}

                    {/* Client Requirements/Description */}
                    {lead.description && (
                      <div style={{ marginTop: '12px', padding: '12px', background: '#e0f2fe', borderRadius: '8px', borderLeft: '4px solid #0284c7' }}>
                        <p style={{ fontSize: '12px', color: '#0369a1', margin: '0 0 6px 0', fontWeight: '700', textTransform: 'uppercase' }}>📝 Client Requirements</p>
                        <p style={{ fontSize: '14px', margin: 0, color: '#0c4a6e', lineHeight: '1.6' }}>{lead.description}</p>
                      </div>
                    )}

                    {/* Extra Fields from Excel */}
                    {lead.extraFields && Object.keys(lead.extraFields).length > 0 && (
                      <div style={{ marginTop: '12px', padding: '12px', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
                        <p style={{ fontSize: '12px', color: '#92400e', margin: '0 0 8px 0', fontWeight: '600' }}>📋 Additional Info</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                          {Object.entries(lead.extraFields).map(([key, value]) => (
                            <div key={key}>
                              <span style={{ fontSize: '11px', color: '#78716c', fontWeight: '600' }}>{key}: </span>
                              <span style={{ fontSize: '12px', color: '#44403c' }}>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {lead.notes && (
                      <div style={{ marginTop: '12px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '3px solid #667eea' }}>
                        <p style={{ fontSize: '12px', color: '#999', margin: '0 0 4px 0' }}>Notes</p>
                        <p style={{ fontSize: '13px', margin: 0, color: '#333' }}>{lead.notes}</p>
                      </div>
                    )}

                    {lead.nextFollowUp && (
                      <div style={{ marginTop: '12px', padding: '10px', background: '#fff3cd', borderRadius: '4px', borderLeft: '3px solid #ffc107' }}>
                        <p style={{ fontSize: '12px', color: '#999', margin: '0 0 4px 0' }}>📞 Next Follow-up</p>
                        <p style={{ fontSize: '14px', margin: 0, color: '#333', fontWeight: '600' }}>{new Date(lead.nextFollowUp).toLocaleDateString()}</p>
                        {lead.followUpNotes && (
                          <p style={{ fontSize: '12px', margin: '4px 0 0 0', color: '#666' }}>{lead.followUpNotes}</p>
                        )}
                      </div>
                    )}

                    <p style={{ fontSize: '11px', color: '#999', marginTop: '10px', marginBottom: 0 }}>
                      Assigned by {lead.assignedBy?.firstName} {lead.assignedBy?.lastName} on {new Date(lead.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginLeft: '15px' }}>
                    <button
                      onClick={() => handleFollowUpClick(lead)}
                      className="btn btn-success"
                      style={{ padding: '8px 16px', fontSize: '12px', backgroundColor: '#28a745' }}
                    >
                      📞 Follow-up
                    </button>
                    <button
                      onClick={() => handleEditClick(lead)}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', fontSize: '12px' }}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 5px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>📞 Schedule Follow-up</h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                Follow-up Date *
              </label>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                Follow-up Notes
              </label>
              <textarea
                value={followUpNotes}
                onChange={(e) => setFollowUpNotes(e.target.value)}
                placeholder="What do you want to discuss in the follow-up? (e.g., Follow up on proposal, Check if client is ready to close)"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '13px',
                  minHeight: '100px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowFollowUpModal(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFollowUp}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Set Follow-up
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyAssignedLeads;
